import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface BarChartProps {
  labels: string[];
  data: number[];
  title: string;
  onBarClick?: (index: number) => void;
  selectedBarIndex?: number | null;
  maxY?: number;
  highlightLastBar?: boolean;
  barColor?: string;
  isPercentage?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  labels,
  data,
  title,
  onBarClick,
  selectedBarIndex,
  maxY,
  highlightLastBar,
  barColor,
  isPercentage,
}) => {
  const [colors, setColors] = useState({
    textColor: '',
    gridColor: '',
  });

  const updateColors = () => {
    const rootStyles = getComputedStyle(document.documentElement);
    setColors({
      textColor: rootStyles.getPropertyValue('--text-color').trim(),
      gridColor: rootStyles.getPropertyValue('--grid-color-chart').trim(),
    });
  };

  useEffect(() => {
    updateColors();
    const observer = new MutationObserver(() => {
      updateColors();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  const processedData: number[] = Array.isArray(data) ? data : [];

  // Asegurar que las etiquetas sean siempre un array para evitar errores
  const processedLabels: string[] = Array.isArray(labels)
    ? labels
    : labels !== undefined && labels !== null
      ? [String(labels)]
      : [];

  const chartData = {
    labels: processedLabels,
    datasets: [
      {
        label: 'Datos de respuesta',
        data: processedData,
        backgroundColor: processedData.map((_, idx) => {
          if (selectedBarIndex !== undefined && selectedBarIndex !== null) {
            return idx === selectedBarIndex ? "#FFC000" : (barColor || "#00A4E4");
          }
          if (highlightLastBar && idx === processedData.length - 1) {
            return "#FFC000";
          }
          return barColor || "#00A4E4";
        }),
        borderColor: colors.textColor,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: colors.textColor,
        },
      },
      title: {
        display: true,
        text: title,
        color: colors.textColor,
      },
      datalabels: {
        display: false,
      },
      customValuePlugin: {},
    },
    scales: {
      y: {
        beginAtZero: true,
        max: isPercentage ? 100 : maxY,
        ticks: {
          color: colors.textColor,
        },
        grid: {
          color: colors.gridColor,
        },
      },
      x: {
        ticks: {
          color: colors.textColor,
          autoSkip: true,
          maxTicksLimit: processedLabels.length,
        },
        grid: {
          color: colors.gridColor,
        },
      },
    },
    onClick: (event: any, elements: any[]) => {
      if (onBarClick && elements && elements.length > 0) {
        const index = elements[0].index;
        onBarClick(index);
      }
    },
  };

  const customValuePlugin = {
    id: 'customValuePlugin',
    afterDatasetsDraw: (chart: any) => {
      const { ctx } = chart;
      chart.data.datasets.forEach((dataset: any, i: number) => {
        const meta = chart.getDatasetMeta(i);
        meta.data.forEach((bar: any, index: number) => {
          const value = dataset.data[index];
          ctx.save();
          ctx.fillStyle = 'white';
          ctx.font = 'bold 16px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(value, bar.x, bar.y + bar.height / 2);
          ctx.restore();
        });
      });
    },
  };

  return <Bar data={chartData} options={options} plugins={[customValuePlugin]} />;
};

export default BarChart;